/**
 * SafariStorageAccessBanner.tsx - Safari Storage Access Info Banner
 *
 * Displays informational banner for Safari users about storage access requirements
 * Shows when app is embedded in iframe and storage access is needed
 */

import { Alert } from "@chakra-ui/react"
import { useAuthContext } from '@/contexts/AuthContext'

export const SafariStorageAccessBanner: React.FC = () => {
    const { storageAccess } = useAuthContext()

    // Only show banner if we need storage access but don't have it
    if (!storageAccess.needsAccess || storageAccess.hasAccess) {
        return null
    }

    // Don't show if API not supported (non-Safari browser)
    if (!storageAccess.isSupported) {
        return null
    }

    return (
        <Alert.Root status="info" width="100%" maxWidth="25rem">
            <Alert.Indicator />
            <Alert.Content>
                <Alert.Title>Safari Privacy Notice</Alert.Title>
                <Alert.Description>
                    When you sign in, Safari will ask for permission to use cookies in this embedded chat.
                    This is a one-time request to enable your session.
                </Alert.Description>
            </Alert.Content>
        </Alert.Root>
    )
}

interface SafariStorageAccessErrorProps {
    error?: string | null
}

/**
 * Shows error message when storage access fails
 */
export const SafariStorageAccessError: React.FC<SafariStorageAccessErrorProps> = ({ error }) => {
    if (!error) {
        return null
    }

    return (
        <Alert.Root status="error" width="100%" maxWidth="25rem">
            <Alert.Indicator />
            <Alert.Content>
                <Alert.Title>Storage Access Required</Alert.Title>
                <Alert.Description>
                    {error}
                    {error.includes('visit this site directly') && (
                        <> You may need to <a
                            href={window.location.origin}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'underline', fontWeight: 'bold' }}
                        >
                            visit this site directly
                        </a> first, then return here to sign in.</>
                    )}
                </Alert.Description>
            </Alert.Content>
        </Alert.Root>
    )
}
